<template>
  <b-form-group
    :label-for="'select-' + uuidv4"
    :label-class="['d-inline', labelClass]"
    class="vueMultipleSelectArea"
    :class="[Object.keys(validationContext.failedRules).length !== 0 ? 'error' : null, formGroupClass, disabled ? 'disabledArea' : null]"
  >
    <template v-if="labelText" #label>
      <div>
        {{ labelText }}
        <span v-if="requiredStar" class="text-danger">*</span>
        <img
          v-if="labelTooltip"
          v-b-tooltip="{ customClass: 'tooltipContainer', trigger: 'hover', placement: 'top', html: true, title: labelTooltip }"
          src="../../../../../public/src/images/icon-info-primary.svg"
          alt="icon-info"
          class="labelTooltipIcon"
        />
      </div>
      <button v-if="labelIcon" v-b-modal="labelInfoModalId" type="button" :aria-label="'btnInfo' + uuidv4" class="btn btn-link btnAgeGroupInfo"></button>
    </template>
    <multiselect
      :id="'select-' + uuidv4"
      :ref="'multiselect-' + uuidv4"
      v-model="localValue"
      :multiple="multiple"
      :searchable="searchable"
      :placeholder="currentPlaceholder"
      :close-on-select="closeOnSelect"
      :track-by="trackBy || valueField"
      :label="label || textField"
      :clear-on-select="clearOnSelect"
      :hide-selected="hideSelected"
      :allow-empty="allowEmpty"
      :reset-after="resetAfter"
      :taggable="taggable"
      :tag-placeholder="tagPlaceholder"
      :options="sortedOptions"
      :tag-position="tagPosition"
      :max="max"
      :options-limit="optionsLimit"
      :group-label="groupLabel"
      :group-select="groupSelect"
      :block-keys="computedBlockKeys"
      :internal-search="internalSearch"
      :preserve-search="preserveSearch"
      :preselect-first="preselectFirst"
      :prevent-autofocus="preventAutofocus"
      :limit="limit"
      :limit-text="limitText"
      :disabled="disabled"
      :open-direction="openDirection"
      :max-height="maxHeight"
      :show-no-options="showNoOptions"
      :show-no-results="showNoResults"
      :option-height="optionHeight"
      :class="selectClass"
      :style="{ width: dataWidth, maxWidth: dataWidth }"
      @select="handleSelect"
      @remove="handleRemove"
      @search-change="handleSearchChange"
      @tag="handleTag"
      @open="handleOpen"
      @close="handleClose"
    >
      <template #beforeList>
        <li
          v-if="cleanOption && multiple"
          class="selectionButtonArea d-flex justify-content-between"
          :class="disabled || options.some((option) => option.$isDisabled) ? 'disabledSelectionButtonArea' : null"
        >
          <button type="button" class="btn btnSelectAll pr-0" :disabled="disabled || options.some((option) => option.$isDisabled)" @click="selectAll">
            Tümünü Seç
          </button>
          <button type="button" class="btn btnDeselectAll pl-0" :disabled="disabled || options.some((option) => option.$isDisabled)" @click="deselectAll">
            Tümünü Kaldır
          </button>
        </li>
        <li v-else-if="cleanOption" class="selectionButtonArea">
          <button type="button" class="btn btnDeselectAll w-100" :disabled="disabled || options.some((option) => option.$isDisabled)" @click="deselect">
            Seçimi Kaldır
          </button>
        </li>
      </template>
      <template #noResult="{ search }">
        <span class="noResult" :title="search.trim().toLocaleUpperCase('tr-TR')">SONUÇ BULUNAMADI '{{ search.trim().toLocaleUpperCase("tr-TR") }}'</span>
      </template>
      <template #noOptions>
        <span class="noOptionsResult" title="SEÇENEK BULUNAMADI">{{ noOptionsText }}</span>
      </template>
      <template v-if="multiple" #option="{ option }">
        <span class="option__desc">
          <span class="option__title">
            <span class="textOption" :title="option[textField]">{{ option[textField] }}</span>
          </span>
        </span>
      </template>
    </multiselect>
    <b-form-invalid-feedback :id="'select-' + uuidv4" class="invalid-feedback-inline" :class="validationContext.errors[0] ? 'd-block' : null">
      {{ validationContext.errors[0] ? validationContext.errors[0] : null }}
    </b-form-invalid-feedback>
  </b-form-group>
</template>

<script>
import Multiselect from "../../../../util/vue-multiselect.min";
import helper from "../../../../util/helper";
import { createPopper } from "@popperjs/core";

Multiselect.props.selectLabel.default = "Seçmek için Enter'a basın";
Multiselect.props.selectGroupLabel.default = "Grubu seçmek için Enter'a basın";
Multiselect.props.selectedLabel.default = "Seçilmiş";
Multiselect.props.deselectLabel.default = "";
Multiselect.props.deselectGroupLabel.default = "";

export default {
  name: "BaseVueMultiSelect",
  components: { Multiselect },
  mixins: [helper],
  props: {
    validationContext: {
      type: Object,
      required: true,
    },
    value: {
      type: [Array, String, Number, Object],
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "Seçiniz",
    },
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    trackBy: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    clearOnSelect: {
      type: Boolean,
      default: false,
    },
    hideSelected: {
      type: Boolean,
      default: false,
    },
    allowEmpty: {
      type: Boolean,
      default: true,
    },
    resetAfter: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    tagPlaceholder: {
      type: String,
      default: "Etiket oluşturmak için enter tuşuna basın",
    },
    tagPosition: {
      type: String,
      default: "top",
    },
    max: {
      type: Number,
      default: null,
    },
    optionsLimit: {
      type: Number,
      default: 1000,
    },
    groupLabel: {
      type: String,
      default: null,
    },
    groupSelect: {
      type: Boolean,
      default: false,
    },
    blockKeys: {
      type: Array,
      default: () => [],
    },
    internalSearch: {
      type: Boolean,
      default: false,
    },
    preserveSearch: {
      type: Boolean,
      default: false,
    },
    preselectFirst: {
      type: Boolean,
      default: false,
    },
    preventAutofocus: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: 99999,
    },
    limitText: {
      type: [Function, String],
      default: (count) => `${count} seçildi`,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    openDirection: {
      type: String,
      default: "",
    },
    maxHeight: {
      type: Number,
      default: 264,
    },
    optionHeight: {
      type: Number,
      default: 55,
    },
    selectClass: {
      type: [String, Array],
      default: null,
    },
    dataWidth: {
      type: String,
      default: null,
    },
    textField: {
      type: String,
      default: "name",
    },
    valueField: {
      type: String,
      default: "id",
    },
    showNoOptions: {
      type: Boolean,
      default: true,
    },
    showNoResults: {
      type: Boolean,
      default: true,
    },
    vid: {
      type: String,
      default: null,
    },
    labelText: {
      type: String,
      default: null,
    },
    rules: {
      type: [String, Object],
      default: null,
    },
    customMessages: {
      type: Object,
      default: () => {},
    },
    providerMode: {
      type: String,
      default: "eager",
    },
    formGroupClass: {
      type: [String, Array],
      default: null,
    },
    requiredStar: {
      type: Boolean,
      default: false,
    },
    labelIcon: {
      type: Boolean,
      default: false,
    },
    // Small info icon next to the label; shows the given text as a tooltip on hover.
    // A lighter, tooltip-based alternative to labelIcon (which opens a modal).
    labelTooltip: {
      type: String,
      default: null,
    },
    labelClass: {
      type: [String, Array],
      default: null,
    },
    labelInfoModalId: {
      type: String,
      default: null,
    },
    cleanOption: {
      type: Boolean,
      default: true,
    },
    enableSort: {
      type: Boolean,
      default: true,
    },
    noOptionsText: {
      type: String,
      default: "SEÇENEK BULUNAMADI",
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    appendToBodyPlacement: {
      type: String,
      default: "bottom-start",
    },
    sortBy: {
      type: String,
      default: "",
    },
    isSortDesc: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPlaceholder: this.placeholder,
      popperInstance: null,
      dropdownObserver: null,
    };
  },
  computed: {
    localValue: {
      get() {
        if (this.value === null || this.value === undefined) return this.multiple ? [] : null;

        // Eğer 'multiple' özelliği varsa, ve 'value' bir dizi ise, her bir öğe için 'option' bulmaya çalış
        if (this.multiple && Array.isArray(this.value)) {
          return this.options.filter((option) => this.value.includes(this.getNestedValue(option, this.valueField)));
        }

        // Değer tekil bir öğe ise, mevcut olan 'option' ile eşleşeni döndür
        return this.options.find((option) => this.getNestedValue(option, this.valueField) === this.value) || null;
      },
      set(newValue) {
        this.updateValue(newValue);
      },
    },
    computedBlockKeys() {
      if (this.blockKeys.length > 0) {
        return this.blockKeys;
      }
      return this.multiple ? ["Delete"] : [];
    },
    getNestedValue() {
      return (obj, path) => {
        return path.split(".").reduce((acc, part) => acc && acc[part], obj);
      };
    },
    sortedOptions() {
      if (this.enableSort && this.sortBy) {
        return [...this.options].sort((a, b) => {
          const textA = a[this.sortBy]?.toString().toLocaleLowerCase("tr-TR") || "";
          const textB = b[this.sortBy]?.toString().toLocaleLowerCase("tr-TR") || "";
          return this.isSortDesc ? textB.localeCompare(textA, "tr-TR") : textA.localeCompare(textB, "tr-TR");
        });
      } else if (this.enableSort) {
        return [...this.options].sort((a, b) => {
          const textA = a[this.textField]?.toString().toLocaleLowerCase("tr-TR") || "";
          const textB = b[this.textField]?.toString().toLocaleLowerCase("tr-TR") || "";
          return this.isSortDesc ? textB.localeCompare(textA, "tr-TR") : textA.localeCompare(textB, "tr-TR");
        });
      } else {
        return this.options;
      }
    },
  },
  //mounted() {
  //  if (this.appendToBody) {
  //    window.addEventListener("scroll", this.repositionDropDown);
  //    window.addEventListener("resize", this.repositionDropDown);
  //  }
  //},

  //destroyed() {
  //  if (this.appendToBody) {
  //    window.removeEventListener("scroll", this.repositionDropDown);
  //    window.addEventListener("resize", this.repositionDropDown);
  //  }
  //},
  methods: {
    selectAll() {
      if (this.options.some((option) => option.$isDisabled)) {
        return;
      }

      this.localValue = [...this.options];
    },
    deselectAll() {
      if (this.options.some((option) => option.$isDisabled)) {
        return;
      }
      this.localValue = this.multiple ? [] : null;
    },
    deselect() {
      if (this.options.some((option) => option.$isDisabled)) {
        return;
      }
      this.localValue = null;
    },
    updateValue(newValue) {
      if (this.multiple && Array.isArray(newValue)) {
        // Eğer 'multiple' ise ve 'newValue' bir dizi ise, o diziyi doğrudan geç
        this.$emit(
          "input",
          newValue.map((option) => this.getNestedValue(option, this.valueField) || option)
        );
      } else {
        // Değer tekilse, objenin 'valueField' değerini al
        this.$emit("input", newValue ? this.getNestedValue(newValue, this.valueField) : null);
      }
      //this.appendToBodyScrollEvent();
    },
    handleSelect(selectedOption) {
      if (!this.multiple && selectedOption && typeof selectedOption === "object") {
        this.$emit("select", this.getNestedValue(selectedOption, this.valueField));
      } else if (this.multiple && Array.isArray(selectedOption)) {
        this.$emit(
          "select",
          selectedOption.map((option) => this.getNestedValue(option, this.valueField) || option)
        );
      } else {
        this.$emit("select", selectedOption);
      }
    },
    handleRemove(removedOption) {
      this.$emit("remove", removedOption);
    },
    handleSearchChange(searchQuery) {
      this.$emit("searchChange", searchQuery);
    },
    handleTag(tagName) {
      this.$emit("tag", tagName);
    },
    handleOpen(id) {
      if (this.searchable) {
        this.currentPlaceholder = "Arama";
      }
      this.searchInputIconAdd(id);
      //this.appendToBodyScrollEvent();
      this.appendToBodyOpenEvent(id);
      this.$emit("open", id);
    },
    handleClose(value, id) {
      this.currentPlaceholder = this.placeholder;
      this.searchInputIconRemove(id);
      this.appendToBodyCloseEvent(id);
      this.$emit("close", value, id);
    },
    searchInputIconRemove(id) {
      const searchInput = document.querySelector(`#${id}.multiselect__input`);
      if (searchInput) {
        searchInput.classList.remove("searchInputIcon");
      }
    },
    searchInputIconAdd(id) {
      const searchInput = document.querySelector(`#${id}.multiselect__input`);
      if (searchInput) {
        searchInput.classList.add("searchInputIcon");
      }
    },
    appendToBodyOpenEvent(id) {
      if (this.appendToBody) {
        const refKey = "multiselect-" + id.replace("select-", "");
        const multiselectRef = this.$refs[refKey];
        if (!multiselectRef) {
          console.error(`Multiselect reference not found: ${refKey}`);
          return;
        }

        const dropdown = multiselectRef.$refs.list;
        const tags = multiselectRef.$refs.tags;
        if (dropdown && this.appendToBody) {
          this.dropdownObserver = new MutationObserver(() => {
            if (!dropdown.classList.contains("vueMultipleSelectAppendToBodyArea")) {
              // Dropdown'a yeni class ekle
              dropdown.classList.add("vueMultipleSelectAppendToBodyArea");
              if (this.multiple) {
                dropdown.classList.add("multipleSelectWrapper");
              }
            }
          });

          // Observer'ı başlat
          this.dropdownObserver.observe(dropdown, { attributes: true });
          // Dropdown'un genişliğini tags genişliğiyle eşle
          //dropdown.style.width = `${tags.offsetWidth}px`;
          // dropdown genişliği 200px'den küçükse sabit genişlik veriyor
          const dropdownWidth = tags.offsetWidth < 200 ? 216 : tags.offsetWidth;
          dropdown.style.width = `${dropdownWidth}px`;
          // Dropdown'u body'ye taşı
          document.body.appendChild(dropdown);
          // Popper.js instance'ını oluştur
          dropdown.classList.add("d-none");
          this.popperInstance = createPopper(tags, dropdown, {
            placement: this.appendToBodyPlacement,
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 3],
                },
              },
              {
                name: "preventOverflow",
                options: {
                  boundary: "viewport",
                },
              },
              {
                name: "computeStyles",
                options: {
                  gpuAcceleration: false,
                },
              },
            ],
          });
          // Popper.js konumlandırma işlemini bitirdiğinde göster
          setTimeout(() => {
            dropdown.classList.remove("d-none");
          });
        }
      }
    },
    appendToBodyCloseEvent(id) {
      if (this.appendToBody) {
        const refKey = "multiselect-" + id.replace("select-", "");
        const multiselectRef = this.$refs[refKey];
        if (!multiselectRef) {
          console.error(`Multiselect reference not found: ${refKey}`);
          return;
        }
        const dropdown = multiselectRef.$refs.list;

        if (dropdown && this.popperInstance && this.appendToBody) {
          this.popperInstance.destroy(); // Popper instance'ını temizle
          this.popperInstance = null;

          // MutationObserver'ı durdur
          if (this.dropdownObserver) {
            this.dropdownObserver.disconnect(); // Observer'ı devre dışı bırak
            this.dropdownObserver = null; // Referansı temizle
          }
          //setTimeout(() => {
          //  multiselectRef.$el.appendChild(dropdown); // Dropdown'u eski yerine taşı
          //}, 600);
        }
      }
    },
    //appendToBodyScrollEvent() {
    //  try {
    //    if (this.appendToBody) {
    //      if (document.documentElement.scrollHeight <= document.documentElement.clientHeight) {
    //        this.$nextTick(() => {
    //          window.dispatchEvent(new Event("resize"));
    //        });
    //      } else {
    //        window.scrollBy(0, 1);
    //        this.$nextTick(() => {
    //          window.scrollBy(0, -1);
    //        });
    //      }
    //    }
    //  } catch (error) {
    //    console.error("Scroll or resize operation failed appendToBodySelect:", error);
    //  }
    //},

    //repositionDropDown() {
    //  if (!this.appendToBody) return;
    //
    //  const { top, height } = this.$el.getBoundingClientRect();
    //  const ref = this.$refs["multiselect-" + this.uuidv4];
    //  ref.$refs.list.style.width = `${this.$el.clientWidth}px`;
    //  ref.$refs.list.style.position = "fixed";
    //  ref.$refs.list.style.bottom = "auto";
    //  ref.$refs.list.style.top = `${top + height}px`;
    //},
  },
};
</script>

<style scoped lang="scss">
.labelTooltipIcon {
  width: 14px;
  height: 14px;
  margin-left: 4px;
  margin-top: -2px;
  cursor: help;
}

.tooltipContainer {
  opacity: 1 !important;

  .tooltip-inner {
    text-align: left;
  }
}
</style>
